import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Lock, UserPlus, Trash, Eye, EyeSlash, Key, ArrowLeft, ClockCounterClockwise } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ControlPanel() {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [pinError, setPinError] = useState('');
  const [tab, setTab] = useState('accounts');

  // Accounts state
  const [accounts, setAccounts] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  // Activity state
  const [activity, setActivity] = useState([]);

  // Change PIN state
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinMsg, setPinMsg] = useState('');

  const headers = { 'X-Control-Pin': pin };

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/control/accounts`, { headers: { 'X-Control-Pin': pin } });
      setAccounts(res.data);
    } catch (e) { console.error(e); }
  }, [pin]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/control/activity`, { headers: { 'X-Control-Pin': pin } });
      setActivity(res.data);
    } catch (e) { console.error(e); }
  }, [pin]);

  useEffect(() => {
    if (authenticated) {
      fetchAccounts();
      fetchActivity();
    }
  }, [authenticated, fetchAccounts, fetchActivity]);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError('');
    try {
      await axios.post(`${API}/control/verify-pin`, { pin });
      setAuthenticated(true);
    } catch (err) {
      setPinError(err.response?.data?.detail || 'Invalid PIN');
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/control/accounts`, { username: newUsername, password: newPassword }, { headers });
      setNewUsername('');
      setNewPassword('');
      fetchAccounts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create account');
    } finally { setCreating(false); }
  };

  const handleDeleteAccount = async (accountId, username) => {
    if (!window.confirm(`Delete login account "${username}"?`)) return;
    try {
      await axios.delete(`${API}/control/accounts/${accountId}`, { headers });
      fetchAccounts();
    } catch (err) { alert('Failed to delete account'); }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    setPinMsg('');
    if (newPin !== confirmPin) { setPinMsg('New PINs do not match'); return; }
    if (newPin.length < 4) { setPinMsg('PIN must be at least 4 characters'); return; }
    try {
      await axios.post(`${API}/control/change-pin`, { current_pin: currentPin, new_pin: newPin });
      setPin(newPin);
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
      setPinMsg('PIN changed successfully!');
    } catch (err) {
      setPinMsg(err.response?.data?.detail || 'Failed to change PIN');
    }
  };

  // PIN entry screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#1c1917] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Control Panel
            </h1>
            <p className="text-[11px] text-stone-500 mt-1 uppercase tracking-[0.15em] font-medium">Restricted Access</p>
          </div>

          <div className="bg-white border border-stone-200 p-8">
            <div className="flex items-center gap-2 mb-6">
              <Key size={18} className="text-[#002FA7]" weight="bold" />
              <h2 className="text-sm font-bold text-stone-900 uppercase tracking-wider">Enter PIN</h2>
            </div>

            {pinError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-medium" data-testid="pin-error">
                {pinError}
              </div>
            )}

            <form onSubmit={handlePinSubmit} className="space-y-4">
              <input
                data-testid="control-pin-input"
                type="password"
                value={pin}
                onChange={e => setPin(e.target.value)}
                className="w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2.5 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none transition-all text-center tracking-[0.3em] text-lg"
                placeholder="****"
                required
                autoFocus
              />
              <button type="submit" data-testid="pin-submit"
                className="w-full py-2.5 text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002480] transition-all">
                Unlock
              </button>
            </form>
          </div>

          <button onClick={() => navigate('/')}
            className="mt-6 mx-auto flex items-center gap-1.5 text-stone-500 hover:text-stone-300 text-sm transition-colors">
            <ArrowLeft size={14} /> Back to app
          </button>
        </div>
      </div>
    );
  }

  // Authenticated control panel
  const tabs = [
    { id: 'accounts', label: 'Login Accounts', icon: UserPlus },
    { id: 'activity', label: 'Login Activity', icon: ClockCounterClockwise },
    { id: 'pin', label: 'Change PIN', icon: Key },
  ];

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      <div className="bg-[#1c1917] text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Lock size={18} weight="bold" className="text-[#002FA7]" />
          <h1 className="text-base font-bold tracking-tight">Control Panel</h1>
        </div>
        <button onClick={() => navigate('/')} className="text-stone-400 hover:text-white text-sm flex items-center gap-1.5 transition-colors">
          <ArrowLeft size={14} /> Back to App
        </button>
      </div>

      <div className="max-w-[1000px] mx-auto p-6 sm:p-8">
        <div className="flex gap-0 border-b border-stone-200 mb-6 overflow-x-auto">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'activity') fetchActivity(); }}
                data-testid={`control-tab-${t.id}`}
                className={`px-5 py-2.5 text-sm font-semibold transition-all border-b-2 -mb-px whitespace-nowrap flex items-center gap-2 ${
                  tab === t.id ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}>
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Accounts Tab */}
        {tab === 'accounts' && (
          <div>
            <div className="bg-white border border-stone-200 p-6 mb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-4">Create New Login Account</h3>
              <form onSubmit={handleCreateAccount} className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Username</label>
                  <input data-testid="new-account-username" type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)}
                    className="w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 focus:border-[#002FA7] outline-none transition-all"
                    placeholder="Login username" required />
                </div>
                <div className="flex-1 relative">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Password</label>
                  <input data-testid="new-account-password" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    className="w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 pr-9 focus:border-[#002FA7] outline-none transition-all"
                    placeholder="Login password" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-[30px] text-stone-400 hover:text-stone-600">
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <button type="submit" data-testid="create-account-submit" disabled={creating}
                  className="px-5 py-2 text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002480] transition-all whitespace-nowrap disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Account'}
                </button>
              </form>
            </div>

            <div className="bg-white border border-stone-200">
              <div className="px-6 py-4 border-b border-stone-200">
                <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500">Existing Accounts ({accounts.length})</h3>
              </div>
              {accounts.length === 0 ? (
                <p className="py-10 text-center text-stone-400 text-sm">No login accounts created yet</p>
              ) : (
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-stone-200 bg-stone-50">
                      <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider">Username</th>
                      <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider">Created</th>
                      <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(a => (
                      <tr key={a.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                        <td className="py-3 px-4 font-semibold text-stone-900" data-testid={`account-${a.username}`}>{a.username}</td>
                        <td className="py-3 px-4 text-stone-500 text-xs">
                          {new Date(a.created_at).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button onClick={() => handleDeleteAccount(a.id, a.username)} data-testid={`delete-account-${a.id}`}
                            className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                            <Trash size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {tab === 'activity' && (
          <div className="bg-white border border-stone-200">
            <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500">Login Activity Log</h3>
              <button onClick={fetchActivity} className="text-xs text-[#002FA7] font-semibold hover:underline">Refresh</button>
            </div>
            {activity.length === 0 ? (
              <p className="py-10 text-center text-stone-400 text-sm">No login activity recorded yet</p>
            ) : (
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider">Time</th>
                    <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider">Username</th>
                    <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider">Action</th>
                    <th className="py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider">IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map(a => (
                    <tr key={a.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                      <td className="py-2.5 px-4 text-stone-600 text-xs">{new Date(a.timestamp).toLocaleString()}</td>
                      <td className="py-2.5 px-4 font-semibold text-stone-900">{a.username}</td>
                      <td className="py-2.5 px-4">
                        <span className={`text-xs font-semibold px-2 py-0.5 ${
                          a.action === 'login_success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {a.action === 'login_success' ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-stone-500 text-xs font-mono">{a.ip || '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Change PIN Tab */}
        {tab === 'pin' && (
          <div className="bg-white border border-stone-200 p-6 max-w-md">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-4">Change Control Panel PIN</h3>
            {pinMsg && (
              <div className={`mb-4 p-3 text-xs font-medium border ${
                pinMsg.includes('success') ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
              }`} data-testid="pin-change-message">{pinMsg}</div>
            )}
            <form onSubmit={handleChangePin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Current PIN</label>
                <input data-testid="current-pin-input" type="password" value={currentPin} onChange={e => setCurrentPin(e.target.value)}
                  className="w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 focus:border-[#002FA7] outline-none transition-all"
                  required />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">New PIN</label>
                <input data-testid="new-pin-input" type="password" value={newPin} onChange={e => setNewPin(e.target.value)}
                  className="w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 focus:border-[#002FA7] outline-none transition-all"
                  required />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Confirm New PIN</label>
                <input data-testid="confirm-pin-input" type="password" value={confirmPin} onChange={e => setConfirmPin(e.target.value)}
                  className="w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 focus:border-[#002FA7] outline-none transition-all"
                  required />
              </div>
              <button type="submit" data-testid="change-pin-submit"
                className="px-5 py-2 text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002480] transition-all">
                Change PIN
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
